import React, { useCallback, useEffect, useRef, useState } from "react";
import { Sparkles, X, RefreshCw, Send, TrendingUp, TrendingDown, Minus, ShieldAlert, Target, Anchor, ArrowUpRight, ArrowDownRight, Info } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const isBuy = (t) => String(t ?? "").toLowerCase().includes("buy") || t === 0 || t === "0";
const num = (v) => (v == null || v === "" ? null : Number(v));
const REGEN_MOVE_PCT = 0.2;   // regenerate analysis when price moves > 0.2% ...
const REGEN_MIN_MS = 30000;   // ... and at least 30s since the last generation.

const biasMeta = {
  BULLISH: { icon: TrendingUp, color: "var(--ws-cyan)", label: "Bullish" },
  BEARISH: { icon: TrendingDown, color: "#f87171", label: "Bearish" },
  NEUTRAL: { icon: Minus, color: "#8496b5", label: "Neutral" },
};
const riskColor = (r) => (r === "Low" ? "var(--ws-cyan)" : r === "High" ? "#f87171" : "#fbbf24");

const Zone = ({ icon: Icon, label, value, note, color }) => (
  <div className="ws-price-cell p-3">
    <div className="flex items-center gap-1.5 mb-1">
      <Icon className="w-3.5 h-3.5" style={{ color }} />
      <span className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--ws-muted)]">{label}</span>
    </div>
    <div className="ws-heading text-[16px] font-bold text-white tabular-nums">{value ?? "—"}</div>
    {note && <div className="text-[11px] text-[color:var(--ws-muted)] leading-snug mt-0.5">{note}</div>}
  </div>
);

const PositionWSView = ({ position, onClose }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stale, setStale] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const lastRef = useRef({ price: null, at: 0 });
  const chatEndRef = useRef(null);

  const buy = isBuy(position.type);
  const entry = num(position.price_open);
  const current = num(position.price_current);
  const profit = num(position.profit);
  const volume = num(position.volume);
  const diff = entry != null && current != null ? current - entry : null;
  const diffPct = diff != null && entry ? (diff / entry) * 100 : null;
  const fav = diff == null ? true : buy ? diff >= 0 : diff <= 0;

  const payload = useCallback((extra = {}) => ({
    ticket: position.ticket, symbol: position.symbol, type: position.type,
    entry, current, volume, profit, ...extra,
  }), [position.ticket, position.symbol, position.type, entry, current, volume, profit]);

  const runAnalysis = useCallback(async (nocache = false) => {
    setLoading(true); setStale(false);
    try {
      const res = await fetch(`${API}/mt5/position-analysis`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload({ nocache })),
      });
      if (res.ok) {
        setAnalysis(await res.json());
        lastRef.current = { price: current, at: Date.now() };
      }
    } catch { /* keep prior */ }
    setLoading(false);
  }, [payload, current]);

  // First analysis when the panel opens / position changes.
  useEffect(() => { setAnalysis(null); setMessages([]); lastRef.current = { price: null, at: 0 }; runAnalysis(false); }, [position.ticket]); // eslint-disable-line

  // Live: mark stale / auto-regenerate only on a MEANINGFUL move (not every 1s tick).
  useEffect(() => {
    const last = lastRef.current;
    if (last.price == null || current == null) return;
    const movePct = Math.abs((current - last.price) / last.price) * 100;
    if (movePct <= 0) return;
    if (movePct > REGEN_MOVE_PCT) {
      setStale(true);
      if (Date.now() - last.at > REGEN_MIN_MS && !loading) runAnalysis(true);
    }
  }, [current]); // eslint-disable-line

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, chatBusy]);

  const sendChat = async () => {
    const q = input.trim();
    if (!q || chatBusy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setChatBusy(true);
    try {
      const res = await fetch(`${API}/mt5/position-chat`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload({ question: q })),
      });
      const data = res.ok ? await res.json() : { answer: "Something went wrong. Please try again." };
      setMessages((m) => [...m, { role: "ai", text: data.answer }]);
    } catch {
      setMessages((m) => [...m, { role: "ai", text: "Network error — please try again." }]);
    }
    setChatBusy(false);
  };

  const bm = biasMeta[analysis?.bias] || biasMeta.NEUTRAL;
  const BiasIcon = bm.icon;
  const suggestions = ["Should I trail my stop?", "Where is my risk level?", "What if price reverses?"];

  return (
    <div className="fixed inset-0 z-[80] flex justify-end" data-testid="position-wsview-panel">
      <div className="absolute inset-0" style={{ background: "rgba(3,7,18,0.6)", backdropFilter: "blur(3px)" }} onClick={onClose} />
      <div className="relative w-full max-w-[440px] h-full flex flex-col ws-panel ws-wsview-drawer" style={{ borderRadius: 0, borderRight: "none", background: "var(--ws-bg-elev, #0a1120)" }}>
        {/* Header */}
        <div className="p-5 border-b border-[color:var(--ws-border)] shrink-0">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="ws-icon-chip w-9 h-9"><Sparkles className="w-4.5 h-4.5" /></div>
              <div>
                <h3 className="ws-heading text-[17px] font-bold ws-title-gradient leading-tight">WS View</h3>
                <span className="text-[10px] tracking-[0.16em] text-[color:var(--ws-muted)] uppercase">Live position AI</span>
              </div>
            </div>
            <button onClick={onClose} className="ws-btn-ghost p-2 rounded-lg" data-testid="wsview-close-btn"><X className="w-4 h-4" /></button>
          </div>

          {/* Live metrics (update every second via position prop) */}
          <div className="ws-price-cell p-3" data-testid="wsview-live-metrics">
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${buy ? "bg-[rgba(52,211,153,0.12)]" : "bg-[rgba(248,113,113,0.12)]"}`}>
                {buy ? <ArrowUpRight className="w-3.5 h-3.5 ws-up" /> : <ArrowDownRight className="w-3.5 h-3.5 ws-down" />}
              </div>
              <span className="ws-heading font-bold text-white text-[15px]">{position.symbol}</span>
              <span className={`text-[11px] font-semibold ${buy ? "ws-up" : "ws-down"}`}>{buy ? "BUY" : "SELL"}</span>
              <span className="ws-live-dot ml-0.5" />
              <span className="text-[10px] text-[color:var(--ws-muted)]">live</span>
              <span className="ml-auto text-[11px] text-[color:var(--ws-muted)]">{volume != null ? `${volume} lot` : ""}</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-[9px] uppercase tracking-wide text-[color:var(--ws-muted)]">Entry</div>
                <div className="ws-heading text-[13px] font-bold text-white tabular-nums">{position.price_open}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-wide text-[color:var(--ws-muted)]">Current</div>
                <div className="ws-heading text-[13px] font-bold text-white tabular-nums" data-testid="wsview-current-price">{position.price_current}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-wide text-[color:var(--ws-muted)]">Running P&amp;L</div>
                <div className={`ws-heading text-[13px] font-bold tabular-nums ${profit >= 0 ? "ws-up" : "ws-down"}`} data-testid="wsview-running-pnl">{profit >= 0 ? "+" : ""}{profit != null ? profit.toFixed(2) : "—"}</div>
              </div>
            </div>
            {diff != null && (
              <div className={`text-center text-[11px] mt-2 font-semibold ${fav ? "ws-up" : "ws-down"}`}>
                {diff >= 0 ? "+" : ""}{diff.toFixed(Math.abs(entry) >= 100 ? 2 : 5)}{diffPct != null ? ` (${diffPct >= 0 ? "+" : ""}${diffPct.toFixed(2)}%)` : ""} since entry
              </div>
            )}
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold" style={{ color: bm.color, background: "rgba(120,140,180,0.1)", border: `1px solid ${bm.color}44` }} data-testid="wsview-bias">
                <BiasIcon className="w-3.5 h-3.5" /> {bm.label}
              </span>
              {analysis?.risk_level && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold" style={{ color: riskColor(analysis.risk_level), background: "rgba(120,140,180,0.1)", border: `1px solid ${riskColor(analysis.risk_level)}44` }} data-testid="wsview-risk">
                  <ShieldAlert className="w-3.5 h-3.5" /> {analysis.risk_level} risk
                </span>
              )}
            </div>
            <button onClick={() => runAnalysis(true)} disabled={loading} className="ws-btn-ghost px-2.5 py-1.5 rounded-lg text-[11px] font-semibold inline-flex items-center gap-1.5" data-testid="wsview-refresh-btn">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> {loading ? "Analyzing" : "Refresh"}
            </button>
          </div>

          {stale && !loading && (
            <div className="text-[11px] text-[color:#fbbf24] flex items-center gap-1.5"><Info className="w-3.5 h-3.5" /> Price moved — analysis will refresh shortly (or tap Refresh).</div>
          )}

          {loading && !analysis ? (
            <div className="text-[13px] text-[color:var(--ws-muted)] py-8 text-center">Reading the live market for this position…</div>
          ) : analysis ? (
            <>
              {/* Assessment */}
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--ws-cyan)] font-semibold mb-1.5">Position Assessment</div>
                <p className="text-[13px] text-white leading-relaxed" data-testid="wsview-assessment">{analysis.assessment}</p>
                {analysis.risk_reason && <p className="text-[11px] text-[color:var(--ws-muted)] mt-1">{analysis.risk_reason}</p>}
              </div>

              {/* Key zones */}
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--ws-cyan)] font-semibold mb-2">Booking &amp; Protective Zones</div>
                <div className="grid grid-cols-2 gap-2">
                  <Zone icon={Target} label="Potential Booking" value={analysis.booking_area} note={analysis.booking_reason} color="var(--ws-cyan)" />
                  <Zone icon={Anchor} label="Trailing / SL" value={analysis.trailing_area} note={analysis.trailing_reason} color="#fbbf24" />
                </div>
              </div>

              {/* Support / Resistance */}
              {(analysis.resistances?.length > 0 || analysis.supports?.length > 0) && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--ws-cyan)] font-semibold mb-2">Key Support / Resistance</div>
                  <div className="ws-price-cell p-3 space-y-1.5" data-testid="wsview-levels">
                    {(analysis.resistances || []).map((r, i) => (
                      <div key={`r${i}`} className="flex items-center justify-between text-[12px]"><span className="ws-down">Resistance</span><span className="text-white font-semibold tabular-nums">{r}</span></div>
                    ))}
                    {analysis.chart_current != null && (
                      <div className="flex items-center justify-between text-[12px] py-0.5 border-y border-[color:var(--ws-border)]"><span className="text-[color:var(--ws-blue-bright)]">Market</span><span className="text-white font-semibold tabular-nums">{analysis.chart_current}</span></div>
                    )}
                    {(analysis.supports || []).map((s, i) => (
                      <div key={`s${i}`} className="flex items-center justify-between text-[12px]"><span className="ws-up">Support</span><span className="text-white font-semibold tabular-nums">{s}</span></div>
                    ))}
                  </div>
                  {analysis.why_levels && <p className="text-[11px] text-[color:var(--ws-muted)] leading-snug mt-1.5">{analysis.why_levels}</p>}
                  {!analysis.levels_available && <p className="text-[11px] text-[color:var(--ws-muted)] mt-1.5">Live chart levels are limited for this instrument; scenarios are based on your entry, current price and P&amp;L.</p>}
                </div>
              )}

              {/* Scenarios */}
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--ws-cyan)] font-semibold mb-2">Scenarios</div>
                <div className="space-y-1.5" data-testid="wsview-scenarios">
                  <div className="ws-price-cell p-2.5 flex gap-2" style={{ borderLeft: "2px solid var(--ws-cyan)" }}><TrendingUp className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: "var(--ws-cyan)" }} /><span className="text-[12px] text-white leading-snug">{analysis.scenario_bull}</span></div>
                  <div className="ws-price-cell p-2.5 flex gap-2" style={{ borderLeft: "2px solid #f87171" }}><TrendingDown className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: "#f87171" }} /><span className="text-[12px] text-white leading-snug">{analysis.scenario_bear}</span></div>
                  <div className="ws-price-cell p-2.5 flex gap-2" style={{ borderLeft: "2px solid #8496b5" }}><Minus className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: "#8496b5" }} /><span className="text-[12px] text-white leading-snug">{analysis.scenario_neutral}</span></div>
                </div>
              </div>

              {analysis.disclaimer && (
                <p className="text-[10px] text-[color:var(--ws-muted)] leading-snug italic border-t border-[color:var(--ws-border)] pt-3" data-testid="wsview-disclaimer">{analysis.disclaimer}</p>
              )}

              {/* Chat */}
              <div className="pt-2">
                <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--ws-cyan)] font-semibold mb-2">Ask about this position</div>
                <div className="space-y-2 mb-2 max-h-[240px] overflow-y-auto" data-testid="wsview-chat-messages">
                  {messages.length === 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {suggestions.map((s) => (
                        <button key={s} onClick={() => setInput(s)} className="ws-btn-ghost px-2.5 py-1 rounded-full text-[11px]" data-testid="wsview-suggestion">{s}</button>
                      ))}
                    </div>
                  )}
                  {messages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[85%] px-3 py-2 rounded-xl text-[12px] leading-snug ${m.role === "user" ? "bg-[color:var(--ws-blue)] text-white" : "ws-price-cell text-white"}`}>{m.text}</div>
                    </div>
                  ))}
                  {chatBusy && <div className="flex justify-start"><div className="ws-price-cell px-3 py-2 rounded-xl text-[12px] text-[color:var(--ws-muted)]">Thinking…</div></div>}
                  <div ref={chatEndRef} />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sendChat()}
                    placeholder="e.g. What happens if price hits 77500?"
                    className="flex-1 bg-[rgba(10,17,33,0.8)] border border-[color:var(--ws-border)] rounded-lg text-[12px] text-white px-3 py-2 outline-none focus:border-[color:var(--ws-cyan)]"
                    data-testid="wsview-chat-input"
                  />
                  <button onClick={sendChat} disabled={chatBusy || !input.trim()} className="ws-btn-primary p-2.5 rounded-lg shrink-0 disabled:opacity-40" data-testid="wsview-chat-send"><Send className="w-4 h-4" /></button>
                </div>
              </div>
            </>
          ) : (
            <div className="text-[13px] text-[color:var(--ws-muted)] py-8 text-center">Couldn't generate an analysis. Tap Refresh to try again.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PositionWSView;
